import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/form/baseInputs/Button';
import { DesignerLayoutGrid } from '@/components/layout/DesignerLayoutGrid/DesignerLayoutGrid';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { NEW_PROPOSAL_ID } from '@/modules/proposal/proposal.constants';
import {
  CharitableTrustDesignerStages,
  ROUTE_KEYS,
} from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';

import {
  useCharitableTrustDesignerContext,
  useIsCRT,
} from '../CharitableTrustDesignerContext';
import {
  useCreateCharitableTrustProposalMutation,
  useUpdateCharitableTrustProposalMutation,
} from '../graphql/CharitableTrustProposalMutations.generated';
import { DEFAULT_CHARITABLE_TRUST_DESIGNER_BASIC_INFORMATION_FORM } from './CharitableTrustDesignerBasicInformation.constants';
import {
  CharitableTrustDesignerBasicInformationForm,
  NAMESPACE,
} from './CharitableTrustDesignerBasicInformation.types';
import {
  CharitableTrustDesignerBasicInformationColumn,
  CharitableTrustDesignerBeneficiariesColumn,
} from './CharitableTrustDesignerBasicInformation.UI';
import {
  mapExistingProposalDataToFrom,
  mapFormDataToCreateProposalInput,
  mapFormDataToUpdateProposalInput,
} from './CharitableTrustDesignerBasicInformation.utils';
import {
  CharitableTrustDesignerDataQuery,
  useCharitableTrustDesignerDataQuery,
} from './graphql/CharitableTrustDesignerData.generated';

interface CharitableTrustDesignerBasicInformationProps {
  onFormAction?: (
    action: 'cancel' | CharitableTrustDesignerStages,
    proposalId?: string
  ) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export function CharitableTrustDesignerBasicInformation({
  onFormAction,
  onDirtyChange,
}: CharitableTrustDesignerBasicInformationProps) {
  const navigate = useNavigate();
  const { householdId, proposalId, variant } =
    useCharitableTrustDesignerContext();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const isCRT = useIsCRT();
  const canEdit = useCanEditHouseholdData();

  const defaultValues = {
    ...DEFAULT_CHARITABLE_TRUST_DESIGNER_BASIC_INFORMATION_FORM,
    [NAMESPACE]: {
      ...DEFAULT_CHARITABLE_TRUST_DESIGNER_BASIC_INFORMATION_FORM[NAMESPACE],
      name: `Charitable ${
        isCRT ? 'Remainder' : 'Lead'
      } Trust Analysis ${formatDateToMonDDYYYY(new Date())}`,
    },
  };
  const formMethods = useForm<CharitableTrustDesignerBasicInformationForm>({
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const { data: loadedData, loading: loadingData } =
    useCharitableTrustDesignerDataQuery({
      variables: {
        hasProposalID: proposalId !== NEW_PROPOSAL_ID && !!proposalId,
        proposalId,
      },
      onError: (error) => {
        showFeedback(
          'Could not load proposal details.  Please refresh the page and try again.'
        );
        reportError('Caught error loading proposal details', error);
      },
      fetchPolicy: 'cache-and-network',
      onCompleted: (data: CharitableTrustDesignerDataQuery) => {
        const mappedData = mapExistingProposalDataToFrom(data);
        if (mappedData) {
          reset(mappedData);
        } else if (!mappedData && proposalId !== NEW_PROPOSAL_ID) {
          showFeedback(
            'Could not load proposal details.  Please refresh the page and try again.'
          );
          reportError(
            `Got invalid state: got no data for proposal ${proposalId} on load`
          );
        }
      },
    });

  function navigateToModelScenarios(proposalId: string) {
    navigate(
      getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_MODEL_SCENARIOS,
        { householdId, variant, proposalId: proposalId }
      )
    );
  }

  const [createCharitableTrustProposal, { loading: savingCreate }] =
    useCreateCharitableTrustProposalMutation({
      refetchQueries: 'active',
      onError: (error) => {
        const displayVariant = isCRT ? 'CRT' : 'CLT';
        showFeedback(
          `Could not create a ${displayVariant} proposal.  Please try again.`
        );
        reportError(`Caught error when creating a ${displayVariant}`, error);
      },
      onCompleted: (data) => {
        showFeedback('Proposal created', { variant: 'success' });
        const newProposalId = data?.createProposalV2?.id;
        if (!newProposalId) {
          showFeedback(
            'Could not navigate to the newly-created proposal.  Please return to the Proposals tab'
          );
          reportError('Did not get new proposal ID after creating proposal');
          return;
        }

        if (onFormAction) {
          onFormAction(
            CharitableTrustDesignerStages.BASIC_INFORMATION,
            newProposalId
          );
          return;
        }

        navigateToModelScenarios(newProposalId);
      },
    });

  const [updateCharitableTrustProposal, { loading: savingUpdate }] =
    useUpdateCharitableTrustProposalMutation({
      refetchQueries: 'active',
      onError: (error) => {
        const displayVariant = isCRT ? 'CRT' : 'CLT';
        showFeedback(
          `Could not update the ${displayVariant} proposal.  Please try again.`
        );
        reportError(`Caught error when updating the ${displayVariant}`, error);
      },
      onCompleted: (data) => {
        showFeedback('Proposal updated', { variant: 'success' });

        const proposalId = data.updateProposalV2.id;

        if (onFormAction) {
          onFormAction(
            CharitableTrustDesignerStages.BASIC_INFORMATION,
            proposalId
          );
          return;
        }

        navigateToModelScenarios(proposalId);
      },
    });

  const onSubmit = handleSubmit(async (formData) => {
    if (!canEdit) {
      if (formData[NAMESPACE].id === NEW_PROPOSAL_ID) {
        reportError(
          'Invalid state: User without edit permissions cannot create a new proposal'
        );
        return;
      }

      return navigateToModelScenarios(formData[NAMESPACE].id);
    }

    if (formData[NAMESPACE].id === NEW_PROPOSAL_ID) {
      const input = mapFormDataToCreateProposalInput(
        formData,
        householdId,
        isCRT
      );
      return createCharitableTrustProposal({ variables: { input } });
    } else {
      const input = mapFormDataToUpdateProposalInput(
        formData,
        householdId,
        isCRT,
        loadedData
      );
      return updateCharitableTrustProposal({
        variables: {
          input,
          updateDonorProfiles: false,
          householdID: '',
          donorProfiles: [],
        },
      });
    }
  });

  return (
    <HouseholdPermissionAwareDisabledProvider
      forceDisabled={loadingData || savingCreate || savingUpdate}
    >
      <FormProvider {...formMethods}>
        <DesignerLayoutGrid
          LeftColumn={<CharitableTrustDesignerBasicInformationColumn />}
          RightColumn={<CharitableTrustDesignerBeneficiariesColumn />}
          footerLeftAction={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (onFormAction) {
                  onFormAction('cancel');
                  return;
                }

                navigate(
                  getCompletePathFromRouteKey(
                    ROUTE_KEYS.HOUSEHOLD_DETAILS_PROPOSALS,
                    {
                      householdId,
                    }
                  )
                );
              }}
            >
              Cancel
            </Button>
          }
          footerRightAction={
            <Button
              variant="primary"
              size="sm"
              onClick={onSubmit}
              loading={savingCreate || savingUpdate}
            >
              {canEdit ? 'Save and proceed' : 'Next'}
            </Button>
          }
        />
      </FormProvider>
    </HouseholdPermissionAwareDisabledProvider>
  );
}
